/**
 * Was aus einem Formular an einen Dienst geht.
 *
 * Ein nicht ausgefülltes Feld ist im Browser eine leere Zeichenkette. Für die
 * Dienste ist das etwas anderes als "nicht gesetzt": ein leeres Datum scheitert
 * an `IsISO8601`, ein leeres Auswahlfeld an `IsEnum`, und die Antwort ist ein
 * 400 auf ein Feld, das der Benutzer bewusst leer gelassen hat.
 *
 * Deshalb fällt weg, was leer ist — nicht `null`, nicht `0`, nicht `false`:
 * eine Null ist eine Angabe, eine leere Zeichenkette ist keine.
 */
export function ohneLeereFelder<T extends Record<string, unknown>>(werte: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(werte).filter(([, wert]) => wert !== '' && wert !== undefined),
  ) as Partial<T>;
}

const UNERWARTET =
  'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut. ' +
  'Besteht der Fehler weiter, melden Sie bitte Uhrzeit und Schritt.';
const KEINE_VERBINDUNG =
  'Keine Verbindung zum Server. Bitte prüfen Sie die Netzverbindung und versuchen Sie es erneut.';
const DIENST_WEG = 'Ein Dienst ist gerade nicht erreichbar. Bitte versuchen Sie es in einigen Minuten erneut.';

/**
 * Englische Standardtexte — von einem Dienst ohne eigene Meldung, vom
 * Vorschaltserver oder vom Browser. Die Dienste übersetzen sie inzwischen
 * selbst; hier steht das Netz darunter, falls doch einer durchkommt
 * („An unexpected error occurred“, Testgrundlage 2.2, V10).
 */
const STANDARDMELDUNG: Record<string, string> = {
  'internal server error': UNERWARTET,
  'an unexpected error occurred': UNERWARTET,
  'not found': 'Der Eintrag wurde nicht gefunden.',
  forbidden: 'Dafür fehlt Ihnen die Berechtigung.',
  'forbidden resource': 'Dafür fehlt Ihnen die Berechtigung.',
  'bad request': 'Die Anfrage ist ungültig.',
  unauthorized: 'Sie sind nicht mehr angemeldet. Bitte melden Sie sich erneut an.',
  conflict: 'Die Änderung passt nicht zum aktuellen Stand. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
  'too many requests': 'Zu viele Anfragen in kurzer Zeit. Bitte warten Sie einen Moment und versuchen Sie es erneut.',
  'payload too large': 'Die Datei oder die Datenmenge ist zu groß.',
  'bad gateway': DIENST_WEG,
  'service unavailable': DIENST_WEG,
  'gateway timeout': DIENST_WEG,
  'network error': KEINE_VERBINDUNG,
  'failed to fetch': KEINE_VERBINDUNG,
  'typeerror: failed to fetch': KEINE_VERBINDUNG,
  'load failed': KEINE_VERBINDUNG,
  'networkerror when attempting to fetch resource.': KEINE_VERBINDUNG,
};

const deutsch = (meldung: string): string => STANDARDMELDUNG[meldung.trim().toLowerCase()] ?? meldung;

/**
 * Die Begründung, die der Dienst mitgeschickt hat.
 *
 * Sie ist fast immer brauchbarer als ein selbst formulierter Satz — sie nennt
 * das Feld. Nur wenn keine da ist, tritt der Ersatztext ein; ist der Server
 * gar nicht erreichbar, wird das gesagt — „Speichern fehlgeschlagen“ ließe
 * offen, ob es an der Eingabe lag.
 */
export function fehlermeldung(fehler: unknown, ersatz: string): string {
  const f = fehler as {
    data?: { message?: string | string[] };
    status?: number | string;
    originalStatus?: number;
    message?: string;
  };
  const meldung = f?.data?.message;
  if (Array.isArray(meldung) && meldung.length) return meldung.map((m) => deutsch(String(m))).join('. ');
  if (typeof meldung === 'string' && meldung) return deutsch(meldung);

  // RTK Query: kein Netz, keine Antwort in der Zeit, eine Fehlerseite des
  // Vorschaltservers statt JSON.
  if (f?.status === 'FETCH_ERROR') return KEINE_VERBINDUNG;
  if (f?.status === 'TIMEOUT_ERROR') return 'Der Server hat nicht rechtzeitig geantwortet. Bitte versuchen Sie es erneut.';
  const status = typeof f?.status === 'number' ? f.status : f?.originalStatus;
  if (status === 502 || status === 503 || status === 504) return DIENST_WEG;
  // Ein Fehler des Browsers selbst, etwa „Failed to fetch“.
  if (typeof f?.message === 'string' && STANDARDMELDUNG[f.message.trim().toLowerCase()]) return deutsch(f.message);
  return ersatz;
}
